import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { ChevronRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

interface CategoryHeroProps {
  /** Eyebrow micro-label sopra il titolo (es. "Categoria") */
  eyebrow: string
  /** Titolo principale (es. "Capelli") */
  title: string
  /** Variante italic accanto al titolo (es. "secondo Nashi.") */
  titleItalic?: string
  /** Sottotitolo descrittivo */
  intro: string
  /** Path immagine hero — verificata dal media_index.csv */
  image: string
  imageAlt?: string
  /** Numero di prodotti totali nella categoria, per il display editoriale */
  productCount?: number
  /** Breadcrumb */
  breadcrumbs?: { label: string; to?: string }[]
  ariaLabel?: string
}

export function CategoryHero({
  title,
  titleItalic,
  intro,
  image,
  imageAlt,
  breadcrumbs,
  ariaLabel,
}: CategoryHeroProps) {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  })
  const yImage = useTransform(scrollYProgress, [0, 1], ['0%', '14%'])

  return (
    <section
      ref={ref}
      aria-label={ariaLabel ?? `Categoria ${title}`}
      className="relative bg-cream pt-10 md:pt-14 pb-12 md:pb-16 overflow-hidden"
    >
      {/* Background grain — subtle */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(rgba(59,49,46,0.6) 1px, transparent 1px)',
          backgroundSize: '4px 4px',
        }}
      />

      <div className="relative nashi-container">
        {/* Breadcrumb */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <motion.nav
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            aria-label="Breadcrumb"
            className="mb-10 flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-bruno-muted"
          >
            {breadcrumbs.map((crumb, idx) => (
              <span key={idx} className="flex items-center gap-2">
                {crumb.to ? (
                  <LocalizedLink to={crumb.to} className="hover:text-argan-deep transition-colors">
                    {crumb.label}
                  </LocalizedLink>
                ) : (
                  <span className="text-bruno">{crumb.label}</span>
                )}
                {idx < breadcrumbs.length - 1 && (
                  <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                )}
              </span>
            ))}
          </motion.nav>
        )}

        {/* Editorial layout: text floating left + image on right with tag */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          {/* Left rail — heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
            className="lg:col-span-7 relative"
          >
            <h1
              className="mt-6 font-display text-bruno"
              style={{
                fontSize: 'clamp(3.25rem, 9vw, 8.5rem)',
                lineHeight: 0.92,
                letterSpacing: '-0.025em',
              }}
            >
              <span className="block">{title}</span>
              {titleItalic && (
                <span className="block italic font-light text-argan-deep">
                  {titleItalic}
                </span>
              )}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-8 max-w-xl text-bruno-soft text-base md:text-lg leading-relaxed"
            >
              {intro}
            </motion.p>

          </motion.div>

          {/* Right — image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.25, 1, 0.5, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                src={image}
                alt={imageAlt || title}
                style={{ y: yImage }}
                className="absolute inset-0 h-[114%] w-full object-cover"
                loading="eager"
              />
              {/* Soft cream wash on bottom for legibility on next section */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-cream/40 to-transparent"
              />
              {/* Inner argan border on hover/idle */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-3 border border-cream/30 mix-blend-overlay"
              />
            </div>

          </motion.div>
        </div>
      </div>
    </section>
  )
}
